import { csrfHeaders } from "../auth/session-api.js";

/* Tenant mail server administration (PB-2925 / AC-14), over the notification
   service's admin surface.

   The paths sit under `/api/v1/notifications/` because that is the prefix the
   auth BFF proxy forwards to the notification service. The proxy is
   first-prefix-wins and ends in a catch-all `/api/v1/` → playbook-be, so a path
   outside this prefix would be answered by the wrong service. */

const BASE = "/api/v1/notifications/mail";

export type MailSecurityMode = "starttls" | "tls" | "none";
export type MailSecretProvider = "azure_key_vault" | "aws_secrets_manager";

export interface MailCapabilities {
  secretReferences: {
    providers: MailSecretProvider[];
    examples: Partial<Record<MailSecretProvider, string>>;
  };
  sendingControl: {
    enabled: boolean;
  };
}

export interface MailSendingSettings {
  sendingEnabled: boolean;
  revision: number;
  updatedAt: string | null;
}

/** No `tested` member: it is derived server-side and surfaces as `canActivate`. */
export type MailSenderStatus =
  | "draft"
  | "active"
  | "disabled"
  | "superseded"
  | "archived"
  /* PB-5155. A draft the administrator took off the screen. The server excludes
     these from every administrator surface, so the value should never arrive
     here — but the union is closed, and a status it does not admit would make
     `MailSender.status` a lie at runtime while typechecking cleanly. */
  | "discarded";

export interface MailSender {
  id: string;
  /** Administrator-facing name for this server configuration. */
  displayName: string | null;
  /** Server-resolved fallback: display name → From name → address → host:port. */
  resolvedDisplayName: string;
  host: string;
  port: number;
  securityMode: MailSecurityMode;
  username: string | null;
  /** Legacy response alias during rolling deployment. */
  secretUri: string;
  /** Cloud-neutral reference, never the credential. Safe to display. */
  secretReference?: string;
  /** Derived by the server; administrators do not choose it. */
  secretProvider?: MailSecretProvider;
  fromAddress: string;
  fromDisplayName: string | null;
  /** PB-2938. The DKIM selector the customer's mail server signs with. Null
      means not configured, which the sender-records panel reports as such
      rather than as a DKIM failure. */
  dkimSelector: string | null;
  status: MailSenderStatus;
  lastTestOutcome: "passed" | "failed" | null;
  lastTestReason: string | null;
  lastTestAt: string | null;
  supersededBy: string | null;
  /** Latest real send attempt. Connection tests are deliberately excluded. */
  lastProductionUseAt: string | null;
  canActivate: boolean;
  /** PB-5155. The mirror of `canActivate` for a server that was turned off. */
  canEnable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MailDelivery {
  id: string;
  senderId: string;
  /** The sending server's current name, resolved server-side (PB-5368):
      configured name → From name → address → host:port. The server always sends
      it; OPTIONAL here only for the rolling-deployment window, when a new shell
      can briefly be served by a pod that predates the field. Delivery history
      cannot derive this itself — a discarded server is in no sender listing. */
  senderResolvedDisplayName?: string;
  eventType: string | null;
  /* PB-5407. Why an ADDRESSED email was sent (`invitation`, `set_password`,
     `export_ready`). Null on a registry-event delivery, which carries
     `eventType` instead, and on a connection test, which carries neither.

     OPTIONAL, because a notification service older than this field omits it
     entirely. Absent means the same as null — never "connection test", which is
     what reading this wrong looked like. */
  messageKey?: string | null;
  /* PB-5407. True only for a connection test. The client cannot derive this:
     `messageKey` and `eventType` are both null on a test send AND on any
     addressed email recorded before `messageKey` existed. Optional for the same
     rolling-deploy reason as `messageKey`; absent means "not known to be a
     test", which keeps an older service's rows out of that label. */
  isConnectionTest?: boolean;
  recipientAddress: string;
  /* `pending` is a CLAIM the sender writes before submitting, not a result. A
     row that stays pending means the send was interrupted and its outcome is
     unknown — it needs a human look rather than reading as a failure. */
  outcome: "accepted" | "rejected" | "pending" | "skipped";
  failureReason: string | null;
  /** Stored PB-5169 verdict; clients must not infer it from failure text. */
  failureRetryable: boolean | null;
  submittedAt: string;
}

export interface MailDeliveryPage {
  deliveries: MailDelivery[];
  totals: {
    accepted: number;
    rejected: number;
    pending: number;
    skipped: number;
  };
  /* Names what these numbers COVER. Submission outcomes are not deliverability:
     without this a reader takes "0 rejected" for "everything arrived", which is
     a claim the data cannot support. */
  scope: "submission";
}

export interface MailDeliveryScopedTotals {
  total: number;
  accepted: number;
  rejected: number;
  pending: number;
  skipped: number;
}

export interface MailDeliverySearchRequest {
  senderId: string | null;
  outcome: MailDelivery["outcome"] | null;
  /** Omit for all events, null for connection tests, string for one event. */
  eventType?: string | null;
  /** Exact addressed-message key. Mutually exclusive with eventType. */
  messageKey?: string;
  submittedFrom: string | null;
  submittedTo: string | null;
  recipient: string | null;
  limit: number;
  cursor: string | null;
}

export interface MailDeliverySearchPage {
  deliveries: MailDelivery[];
  pagination: {
    limit: number;
    hasMore: boolean;
    nextCursor: string | null;
  };
  totals: {
    matching: MailDeliveryScopedTotals;
    overall: MailDeliveryScopedTotals;
  };
  scope: "submission";
}

export interface CreateMailSenderRequest {
  displayName?: string | null;
  host: string;
  port: number;
  securityMode: MailSecurityMode;
  username: string | null;
  /** Canonical field used by current clients. */
  secretReference?: string;
  /** Rolling-deployment alias accepted by the service. */
  secretUri?: string;
  fromAddress: string;
  fromDisplayName: string | null;
  /** PB-2938. The DKIM selector the customer's mail server signs with.
   *
   *  OPTIONAL on the REQUEST, matching `CreateMailSenderRequest` in
   *  `docs/api/notification.yaml`, where it is nullish. The response type above
   *  keeps it required because the server always sends it. Declaring it required
   *  here too forced the shell to send the key even when empty, and the server
   *  schema is `.strict()` — so a shell rolled out ahead of notification failed
   *  every sender create. Omitting it is what makes the two versions
   *  interoperable. */
  dkimSelector?: string | null;
}

/**
 * PB-5155. The whole object, because the route is a PUT.
 *
 * `dkimSelector` is REQUIRED here, unlike on the create request, and that
 * difference is deliberate. Create makes it optional so a shell rolled out ahead
 * of notification does not fail every save against a `.strict()` schema — a
 * rollout window a brand-new route cannot have. Here the field must be explicit
 * for the opposite reason: this replaces the whole object, so an omitted key
 * CLEARS the stored value rather than leaving it alone. Copying create's
 * omit-when-empty trick would read as "leave it as it was" and do the opposite.
 */
export interface UpdateMailSenderRequest {
  displayName?: string | null;
  host: string;
  port: number;
  securityMode: MailSecurityMode;
  username: string | null;
  secretReference?: string;
  secretUri?: string;
  fromAddress: string;
  fromDisplayName: string | null;
  dkimSelector: string | null;
}

export interface MailMessageSummary {
  key: string;
  title: string;
  description: string;
  /** Whether a tenant may switch it off. Not enforced yet — policy owns that. */
  optional: boolean;
  /**
   * Whether the send pipeline can render this message from an envelope alone.
   *
   * A DIFFERENT question from `optional`, and the console used to answer it
   * with `optional` — badging two non-optional messages "Always sent" when
   * neither can be sent at all. Server-derived from the same predicate the
   * send path and the notification policy consult (PB-4987).
   */
  sendable: boolean;
  notificationType: string | null;
  /**
   * Every value the message may reference, written `{{name}}`.
   *
   * An override may not DROP one either: the server refuses a save that loses a
   * value the shipped copy of that field used. Which ones apply to which field
   * is read off `defaultSubject` / `defaultBody`.
   */
  variables: string[];
  /** Whether this tenant has replaced any part of the shipped wording. */
  customised: boolean;
  /** Which fields they replaced. Per field: an override may change only one. */
  customisedFields: ("subject" | "body")[];
  /** False when security controls require shipped content unchanged. */
  editable?: boolean;
}

/** One message as the editor opens it, and the wording revert would restore. */
export interface MailMessageDetail extends MailMessageSummary {
  /** The effective template, placeholders UNSUBSTITUTED — this is what is edited. */
  subject: string;
  body: string;
  defaultSubject: string;
  defaultBody: string;
}

/**
 * A null field means "this one goes back to tracking the shipped default", so
 * it must be sent explicitly rather than omitted.
 */
export interface MailMessageOverrideRequest {
  subject: string | null;
  body: string | null;
}

export interface MailMessagePreview {
  key: string;
  subject: string;
  /** Plain text, exactly as a recipient would receive it. */
  body: string;
}

export class MailApiError extends Error {
  readonly code: string | undefined;

  constructor(message: string, code: string | undefined) {
    super(message);
    this.name = "MailApiError";
    this.code = code;
  }
}

export const mailSendersQueryKey = ["mail", "senders"] as const;
export const mailCapabilitiesQueryKey = ["mail", "capabilities"] as const;
export const mailSendingSettingsQueryKey = ["mail", "settings"] as const;
/* The tenant's sender inventory and sending setting are TENANT data, and
   `/admin` stays mounted across an in-place session handover — so an unscoped
   entry answered the next tenant with the previous one's servers, and a
   credential dialog could say an email was queued for a tenant with no active
   server (PB-5435 review). Built UNDER the shared prefixes, so the prefix
   invalidations on the Mail server page still reach every tenant's entry.
   Capabilities stay shared: they describe the deployment
   (`sendingControlEnabled()`), not a tenant. */
export const tenantMailSendersQueryKey = (tenantId: string | null) =>
  [...mailSendersQueryKey, tenantId] as const;
export const tenantMailSendingSettingsQueryKey = (tenantId: string | null) =>
  [...mailSendingSettingsQueryKey, tenantId] as const;
export const mailMessagesQueryKey = ["mail", "messages"] as const;
export const mailPreviewQueryKey = (key: string, workspace: string) =>
  ["mail", "messages", key, "preview", workspace] as const;
export const mailMessageQueryKey = (key: string) =>
  ["mail", "messages", key] as const;
export const mailDeliveriesQueryKey = ["mail", "deliveries"] as const;

export type MailSenderListGroup =
  | "default"
  | "operational"
  | "previous"
  | "archived";

export interface MailSenderPage {
  senders: MailSender[];
  pagination: {
    group: MailSenderListGroup;
    limit: number;
    hasMore: boolean;
    nextCursor: string | null;
  };
}

export interface MailSenderPageRequest {
  group: MailSenderListGroup;
  limit: number;
  cursor?: string | null;
}

export async function getMailCapabilities(): Promise<MailCapabilities> {
  return (await request(`${BASE}/capabilities`)) as MailCapabilities;
}

export async function getMailSendingSettings(): Promise<MailSendingSettings> {
  return (await request(`${BASE}/settings`)) as MailSendingSettings;
}

export async function updateMailSendingSettings(input: {
  sendingEnabled: boolean;
  revision: number;
}): Promise<MailSendingSettings> {
  return (await request(`${BASE}/settings`, {
    method: "PUT",
    body: JSON.stringify(input),
  })) as MailSendingSettings;
}

export async function listMailSenders(): Promise<MailSender[]> {
  const body = (await request(`${BASE}/senders`)) as { senders: MailSender[] };
  return body.senders;
}

/** Bounded, stable sender history page used by the administration console. */
export async function listMailSenderPage({
  group,
  limit,
  cursor,
}: MailSenderPageRequest): Promise<MailSenderPage> {
  const params = new URLSearchParams({ group, limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  return (await request(
    `${BASE}/senders?${params.toString()}`,
  )) as MailSenderPage;
}

export async function createMailSender(
  input: CreateMailSenderRequest,
): Promise<MailSender> {
  return (await request(`${BASE}/senders`, {
    method: "POST",
    body: JSON.stringify(input),
  })) as MailSender;
}

export async function testMailSender(senderId: string): Promise<MailSender> {
  return (await request(
    `${BASE}/senders/${encodeURIComponent(senderId)}/test`,
    {
      method: "POST",
    },
  )) as MailSender;
}

/** `supersedesId` is required by the server whenever a sender is already live. */
export async function activateMailSender(
  senderId: string,
  supersedesId: string | null,
): Promise<MailSender> {
  return (await request(
    `${BASE}/senders/${encodeURIComponent(senderId)}/activate`,
    { method: "POST", body: JSON.stringify({ supersedesId }) },
  )) as MailSender;
}

export async function disableMailSender(senderId: string): Promise<MailSender> {
  return (await request(
    `${BASE}/senders/${encodeURIComponent(senderId)}/disable`,
    { method: "POST" },
  )) as MailSender;
}

/**
 * An emptied optional field becomes `null`, never `""`.
 *
 * The server validates these as `.min(1).nullish()`, so an empty string is a
 * 400 while `null` clears the field. A form hands back `""` for a box the
 * administrator cleared, which means "remove this" — so it is translated here
 * rather than at each call site, where one missed field would be a 400 the user
 * cannot act on.
 */
function clearedToNull(value: string | null): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
}

export async function updateMailSender(
  senderId: string,
  input: UpdateMailSenderRequest,
): Promise<MailSender> {
  return (await request(`${BASE}/senders/${encodeURIComponent(senderId)}`, {
    method: "PUT",
    body: JSON.stringify({
      ...input,
      displayName: clearedToNull(input.displayName ?? null),
      username: clearedToNull(input.username),
      fromDisplayName: clearedToNull(input.fromDisplayName),
      dkimSelector: clearedToNull(input.dkimSelector),
    }),
  })) as MailSender;
}

export async function discardMailSender(senderId: string): Promise<MailSender> {
  return (await request(
    `${BASE}/senders/${encodeURIComponent(senderId)}/discard`,
    { method: "POST" },
  )) as MailSender;
}

export async function enableMailSender(senderId: string): Promise<MailSender> {
  return enableMailSenderAndSwitch(senderId, null);
}

export async function enableMailSenderAndSwitch(
  senderId: string,
  switchesFromId: string | null,
): Promise<MailSender> {
  return (await request(
    `${BASE}/senders/${encodeURIComponent(senderId)}/enable`,
    { method: "POST", body: JSON.stringify({ switchesFromId }) },
  )) as MailSender;
}

export async function archiveMailSender(senderId: string): Promise<MailSender> {
  return (await request(
    `${BASE}/senders/${encodeURIComponent(senderId)}/archive`,
    { method: "POST" },
  )) as MailSender;
}

export async function restoreMailSender(senderId: string): Promise<MailSender> {
  return (await request(
    `${BASE}/senders/${encodeURIComponent(senderId)}/restore`,
    { method: "POST" },
  )) as MailSender;
}

export async function listMailMessages(): Promise<MailMessageSummary[]> {
  const body = (await request(`${BASE}/messages`)) as {
    messages: MailMessageSummary[];
  };
  return body.messages;
}

/** `workspace` is the one value a preview renders truthfully. */
export async function previewMailMessage(
  key: string,
  workspace: string,
): Promise<MailMessagePreview> {
  const query = workspace ? `?workspace=${encodeURIComponent(workspace)}` : "";
  return (await request(
    `${BASE}/messages/${encodeURIComponent(key)}/preview${query}`,
  )) as MailMessagePreview;
}

export async function getMailMessage(key: string): Promise<MailMessageDetail> {
  return (await request(
    `${BASE}/messages/${encodeURIComponent(key)}`,
  )) as MailMessageDetail;
}

export async function saveMailMessageOverride(
  key: string,
  input: MailMessageOverrideRequest,
): Promise<MailMessageDetail> {
  return (await request(`${BASE}/messages/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  })) as MailMessageDetail;
}

export async function revertMailMessageOverride(
  key: string,
): Promise<MailMessageDetail> {
  return (await request(`${BASE}/messages/${encodeURIComponent(key)}`, {
    method: "DELETE",
  })) as MailMessageDetail;
}

/**
 * Renders the UNSAVED draft server-side, through the same code a real send
 * uses. The editor never lays a message out itself — a second rendering path
 * would drift from the real one and the drift would only surface when a
 * recipient complained.
 */
export async function previewMailMessageDraft(
  key: string,
  draft: MailMessageOverrideRequest & { workspace: string | null },
): Promise<MailMessagePreview> {
  return (await request(`${BASE}/messages/${encodeURIComponent(key)}/preview`, {
    method: "POST",
    body: JSON.stringify(draft),
  })) as MailMessagePreview;
}

/**
 * The server's maximum window. The table pages through it in the client, and
 * says so when the tenant's history is longer than the window — the endpoint
 * returns recent sends, while `totals` counts everything.
 */
export const MAIL_DELIVERY_WINDOW = 200;

export async function listMailDeliveries(): Promise<MailDeliveryPage> {
  return (await request(
    `${BASE}/deliveries?limit=${MAIL_DELIVERY_WINDOW}`,
  )) as MailDeliveryPage;
}

/** Filtered delivery history. Recipient PII stays in the POST body. */
export async function searchMailDeliveries(
  input: MailDeliverySearchRequest,
): Promise<MailDeliverySearchPage> {
  return (await request(`${BASE}/deliveries/search`, {
    method: "POST",
    body: JSON.stringify(input),
  })) as MailDeliverySearchPage;
}

/** One recipient's standing on one message. Absent means never sent. */
export interface MailDeliveryNotice {
  recipientUserId: string;
  outcome: MailDelivery["outcome"];
  submittedAt: string;
}

/**
 * The server's per-request recipient cap, mirrored so the client can CHUNK
 * rather than trip it. Truncating instead would silently show "never notified"
 * for real members past the cap — the exact wrong answer this lookup exists to
 * prevent, reintroduced one layer up.
 */
export const MAIL_LOOKUP_RECIPIENT_LIMIT = 1000;

/**
 * Which of these recipients were sent `eventType`, and when.
 *
 * The result is a SUBSET: a recipient with no delivery is absent rather than
 * present with a null outcome, so callers must treat "missing" as "never sent"
 * and never as "unknown". A member list longer than the cap is split across
 * several requests and merged, so growth changes the number of round trips and
 * never the answer.
 */
export async function lookupMailDeliveries(
  eventType: string,
  recipientUserIds: readonly string[],
): Promise<MailDeliveryNotice[]> {
  if (recipientUserIds.length === 0) return [];

  const chunks: string[][] = [];
  for (
    let i = 0;
    i < recipientUserIds.length;
    i += MAIL_LOOKUP_RECIPIENT_LIMIT
  ) {
    chunks.push([
      ...recipientUserIds.slice(i, i + MAIL_LOOKUP_RECIPIENT_LIMIT),
    ]);
  }

  const pages = await Promise.all(
    chunks.map(async (chunk) => {
      const page = await request(`${BASE}/deliveries/lookup`, {
        method: "POST",
        body: JSON.stringify({ eventType, recipientUserIds: chunk }),
      });
      return readNotices(page);
    }),
  );
  return pages.flat();
}

/**
 * `notices` is REQUIRED by the contract, so its absence is a broken server, not
 * an empty answer — and the two must not look alike here.
 *
 * Defaulting a malformed body to `[]` would report "nobody was ever notified"
 * for every member on the page: a confident wrong answer, which is the exact
 * failure this whole lookup exists to prevent. Throwing instead surfaces it as
 * the degraded state the Members page already knows how to say out loud.
 */
function readNotices(page: unknown): MailDeliveryNotice[] {
  const notices = (page as { notices?: unknown } | null)?.notices;
  if (!Array.isArray(notices)) throw malformedLookup();
  /* EVERY ENTRY, not just the array. `notices: [null]` cleared the array check
     and then threw while the Members page built its lookup map — a render crash
     rather than the degraded state the page knows how to describe. Each field is
     read here, so a bad entry fails at the boundary where it can be reported. */
  for (const notice of notices) {
    if (
      typeof notice !== "object" ||
      notice === null ||
      typeof (notice as MailDeliveryNotice).recipientUserId !== "string" ||
      typeof (notice as MailDeliveryNotice).outcome !== "string" ||
      typeof (notice as MailDeliveryNotice).submittedAt !== "string" ||
      /* PARSEABLE, not merely a string. A malformed timestamp cleared a
         typeof check and then rendered as "Invalid Date" inside the pill —
         failing in the UI rather than at the boundary that rejects everything
         else malformed. */
      Number.isNaN(Date.parse((notice as MailDeliveryNotice).submittedAt))
    ) {
      throw malformedLookup();
    }
  }
  return notices as MailDeliveryNotice[];
}

function malformedLookup(): MailApiError {
  return new MailApiError(
    "The server returned notification status we could not read.",
    "invalid_delivery_lookup_response",
  );
}

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...csrfHeaders(),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    throw new MailApiError(
      body?.message ?? body?.code ?? `Request failed (${response.status})`,
      body?.code,
    );
  }
  /* Same guard the failure path has: nothing enforces "200 implies JSON" at
     runtime, and an ingress answering 200 with an HTML page would otherwise put
     the engine's `Unexpected token '<'` in front of an administrator. */
  try {
    return await response.json();
  } catch {
    throw new Error("The server returned a response we could not read.");
  }
}
